import React from 'react';
import { NameField } from '../form-fields/NameField';
import { EmailField } from '../form-fields/EmailField';
import { PhoneField } from '../form-fields/PhoneField';
import { TextFieldWithLength } from '../form-fields/TextField';
import { cn } from '../../lib/utils';  // Adjust the path based on your project structure

interface FormField {
  id: string;
  type: 'name' | 'email' | 'phone' | 'text' | 'custom' | 'content-display';  // Added content-display
  required?: boolean;
  label?: string;
  customComponent?: React.ComponentType<any>;
  validate?: (value: string) => string | undefined;
  page: number;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  multiline?: boolean;
  rows?:number;
  height?: string;
  contents?: any[]; // Add this for content display data
}

interface FormPage {
  title: string;
  description?: string;
}

interface FormWrapperProps {
  title: string;
  description?: string;
  fields: FormField[];
  pages: FormPage[];
  onSubmit: (data: Record<string, string>) => Promise<void>;
  submitButtonText?: string;
  className?: string;
  width?: string;
  maxWidth?: string;
  minHeight?: string;
  fieldSpacing?: 'even' | 'default';
  buttonPosition?: 'bottom' | 'after-fields';
  defaultSpacing?: string;
  fullscreen?: boolean; // New prop for fullscreen mode
}


interface FormState {
  data: Record<string, string>;
  errors: Record<string, string>;
  currentPage: number;
  status: 'idle' | 'submitting' | 'success' | 'error';
  error?: string;
}

interface TransitionState {
  currentPage: number;
  nextPage: number | null;
  direction: 'forward' | 'backward';
}



const Notification = ({ status, message, onClose }: {
  status: Exclude<FormState['status'], 'idle'>;
  message: string;
  onClose: () => void;
}) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = status === 'success' ? 'bg-green-500' : 'bg-red-500';

  return (
    <div
      className={`fixed top-4 right-4 p-4 rounded-md  ${bgColor} text-white
        animate-[slideIn_0.5s_ease-out]`}
      role="alert"
    >
      <div className="flex items-center gap-2">
        {status === 'success' ? (
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
          </svg>
        )}
        <span className="font-medium">{message}</span>
        <button onClick={onClose} className="ml-auto hover:opacity-80 transition-opacity">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const defaultValidations = {
  name: (value: string) => {
    if (!value.trim()) return 'Name is required';
    if (value.length < 2) return 'Name must be at least 2 characters';
    return undefined;
  },
  email: (value: string) => {
    if (!value.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
    return undefined;
  },
  phone: (value: string) => {
    if (!value.trim()) return 'Phone is required';
    if (!/^\+?[\d\s-]{10,}$/.test(value)) return 'Invalid phone format';
    return undefined;
  }
};

const PageTransition = ({ 
  children, 
  state,
  direction
}: {
  children: React.ReactNode;
  state: 'entering' | 'exiting' | 'current';
  direction: 'forward' | 'backward';
}) => {
  const getTransform = () => {
    if (state === 'entering') {
      return direction === 'forward' ? 'translate-x-full' : '-translate-x-full';
    }
    if (state === 'exiting') {
      return direction === 'forward' ? '-translate-x-full' : 'translate-x-full';
    }
    return 'translate-x-0';
  };

  return (
    <div
      className={`
        absolute inset-0 w-full
        transition-all duration-700 ease-in-out
        ${state === 'current' ? 'opacity-100 z-10' : 'opacity-0 z-0'}
        transform ${getTransform()}
      `}
    >
      {children}
    </div>
  );
};


export const FormWrapper: React.FC<FormWrapperProps> = ({
  title,
  description,
  fields,
  pages,
  onSubmit,
  submitButtonText = 'Submit',
  className = '',
  width = '100%',
  maxWidth = '56rem',
  minHeight = '100%',
  fieldSpacing = 'default',
  buttonPosition = 'bottom',
  defaultSpacing = '6',
  fullViewport = false // New prop for full viewport mode
}) => {

  const scrollToField = (fieldId: string) => {
    const fieldElement = document.getElementById(`field-${fieldId}`);
    if (fieldElement && formContentRef.current) {
        const fieldRect = fieldElement.getBoundingClientRect();
        const containerRect = formContentRef.current.getBoundingClientRect();
        
        // Calculate the scroll position
        const scrollTop = fieldElement.offsetTop - containerRect.height / 4;
        
        formContentRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
        });
        
        // Add a highlight animation to the field
        fieldElement.classList.add('highlight-field');
        setTimeout(() => {
            fieldElement.classList.remove('highlight-field');
        }, 2000);
    }
};

  const [formState, setFormState] = React.useState<FormState>({
    data: Object.fromEntries(fields.map(field => [field.id, ''])),
    errors: {},
    currentPage: 0,
    status: 'idle'
  });

  const [transitionState, setTransitionState] = React.useState<TransitionState>({
    currentPage: 0,
    nextPage: null,
    direction: 'forward'
  });

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState('auto');
  const [pageHeights, setPageHeights] = React.useState<Record<number, number>>({});



  React.useEffect(() => {
    const updateContentHeight = () => {
        if (contentRef.current) {
            // Find the current page content
            const currentPageContent = Array.from(contentRef.current.children)
                .find(child => {
                    const style = window.getComputedStyle(child as HTMLElement);
                    return style.opacity === '1' && style.transform.includes('translate-x-0');
                });

            if (currentPageContent) {
                const height = currentPageContent.scrollHeight;
                setContentHeight(`${height}px`);
            }
        }
    };

    // Initial height update
    updateContentHeight();

    // Add resize observer to handle dynamic content changes
    const resizeObserver = new ResizeObserver(updateContentHeight);
    if (contentRef.current) {
        Array.from(contentRef.current.children).forEach(child => {
            resizeObserver.observe(child);
        });
    }

    // Cleanup
    return () => {
        resizeObserver.disconnect();
    };
}, [formState.currentPage, formState.errors]);

  const validateField = (field: FormField, value: string) => {
    if (field.required) {
      const validator = field.validate || defaultValidations[field.type];
      return validator ? validator(value) : undefined;
    }
    return undefined;
  };

  const validatePage = (pageNum: number) => {
    const pageFields = fields.filter(f => f.page === pageNum);
    const newErrors: Record<string, string> = {};
    let firstInvalidField: string | null = null;

    pageFields.forEach(field => {
        const error = validateField(field, formState.data[field.id]);
        if (error) {
            newErrors[field.id] = error;
            if (!firstInvalidField) {
                firstInvalidField = field.id;
            }
        }
    });

    setFormState(prev => ({
        ...prev,
        errors: { ...prev.errors, ...newErrors }
    }));

    if (firstInvalidField) {
        scrollToField(firstInvalidField);
    }

    return Object.keys(newErrors).length === 0;
};

  const handleChange = (fieldId: string) => (value: string) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;

    const error = validateField(field, value);

    setFormState(prev => ({
      ...prev,
      data: { ...prev.data, [fieldId]: value },
      errors: { ...prev.errors, [fieldId]: error }
    }));
  };

  const formContentRef = React.useRef<HTMLDivElement>(null);

  const changePage = (nextPage: number) => {
    const direction = nextPage > formState.currentPage ? 'forward' : 'backward';
    
    setTransitionState({
        currentPage: formState.currentPage,
        nextPage,
        direction
    });

    setTimeout(() => {
        setFormState(prev => ({
            ...prev,
            currentPage: nextPage
        }));
        setTransitionState(prev => ({
            ...prev,
            currentPage: nextPage,
            nextPage: null
        }));
        
        // Scroll to top of form content
        if (formContentRef.current) {
            formContentRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, 700);
};

  const handleNextPage = () => {
    if (validatePage(formState.currentPage)) {
      changePage(formState.currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    changePage(formState.currentPage - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePage(formState.currentPage)) {
      return;
    }

    setFormState(prev => ({ ...prev, status: 'submitting' }));

    try {
      await onSubmit(formState.data);
      
      setFormState(prev => ({ 
        ...prev, 
        status: 'success' 
      }));

      setTimeout(() => {
        setFormState({
          data: Object.fromEntries(fields.map(field => [field.id, ''])),
          errors: {},
          currentPage: 0,
          status: 'idle'
        });
        setTransitionState({
          currentPage: 0,
          nextPage: null,
          direction: 'forward'
        });
      }, 1000);

    } catch (error) {
      setFormState(prev => ({ 
        ...prev, 
        status: 'error',
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      }));
    }
  };

  const getPageState = (pageIndex: number): 'entering' | 'exiting' | 'current' => {
    if (transitionState.nextPage === pageIndex) return 'entering';
    if (transitionState.currentPage === pageIndex && transitionState.nextPage !== null) return 'exiting';
    if (transitionState.currentPage === pageIndex) return 'current';
    return 'entering';
  };

  const isLastPage = formState.currentPage === pages.length - 1;
  const isSubmitting = formState.status === 'submitting';

  const ButtonGroup = () => (
    <div className="flex gap-4 w-full">
        {formState.currentPage > 0 && (
            <button
                type="button"
                onClick={handlePrevPage}
                className="flex-1 py-3 px-4 rounded-lg text-gray-700 font-medium border border-gray-300 hover:bg-gray-50 transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
                Previous
            </button>
        )}
        {!isLastPage ? (
            <button
                type="button"
                onClick={handleNextPage}
                className={`flex-1 py-3 px-4 rounded-lg text-white font-medium bg-blue-600 hover:bg-blue-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    formState.currentPage === 0 ? 'ml-auto' : ''
                }`}
            >
                Next
            </button>
        ) : (
            <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 py-3 px-4 rounded-lg text-white font-medium transition duration-200 relative ${
                    isSubmitting ? 'bg-green-500 cursor-wait' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isSubmitting ? 'focus:ring-green-500' : 'focus:ring-blue-500'
                }`}
            >
                <span className={isSubmitting ? 'opacity-0' : 'opacity-100'}>
                    {submitButtonText}
                </span>
                {isSubmitting && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg 
                            className="animate-spin h-5 w-5 text-white" 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24"
                        >
                            <circle 
                                className="opacity-25" 
                                cx="12" 
                                cy="12" 
                                r="10" 
                                stroke="currentColor" 
                                strokeWidth="4"
                            />
                            <path 
                                className="opacity-75" 
                                fill="currentColor" 
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                    </div>
                )}
            </button>
        )}
    </div>
);

return (
  <div 
    className={cn(
      fullViewport ? "w-full h-screen" : "max-w-[1200px] h-[80%]",
      className
    )} 
    style={{ 
      width: fullViewport ? '100%' : width,
      maxWidth: fullViewport ? 'none' : maxWidth
    }}
  >
    <style>{`
      @keyframes highlightField {
        0% { background-color: transparent; }
        50% { background-color: rgba(59, 130, 246, 0.1); }
        100% { background-color: transparent; }
      }
      .highlight-field {
        animation: highlightField 2s ease-in-out;
        border-radius: 0.5rem;
        padding: 0.5rem;
        margin: -0.5rem;
      }
    `}</style>

    {formState.status !== 'idle' && (
      <Notification
        status={formState.status}
        message={
          formState.status === 'success'
            ? 'Form submitted successfully!'
            : formState.status === 'error'
            ? formState.error ?? 'An error occurred'
            : 'Submitting...'
        }
        onClose={() => setFormState(prev => ({ ...prev, status: 'idle' }))}
      />
    )}

    <div 
      className={cn(
        "mx-auto bg-white rounded-2xl overflow-hidden",
        fullViewport ? "h-screen" : "h-full"
      )}
      style={{ 
        maxWidth: fullViewport ? 'none' : maxWidth,
        minHeight: fullViewport ? '100vh' : minHeight,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header Section */}
        <div className={cn(
          "grid grid-cols-5 bg-black bg-opacity-5",
          fullViewport ? "rounded-none" : "rounded-2xl"
        )}>
          {/* Left side - Main title */}
          <div className={cn(
            "col-span-2 flex flex-col bg-black bg-opacity-15 p-8",
            fullViewport ? "rounded-none" : "rounded-2xl"
          )}>
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
            {description && <p className="mt-2 text-gray-600">{description}</p>}
            <div className="mt-6 flex gap-2">
              {pages.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-16 rounded-full transition-all duration-800 ${
                    index === formState.currentPage
                      ? 'bg-blue-600 w-20'
                      : index < formState.currentPage
                      ? 'bg-blue-200'
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right side - Current page title */}
          <div className="col-span-3 flex flex-col justify-start p-8">
            <h2 className="text-2xl font-semibold text-gray-800">
              {pages[formState.currentPage].title}
            </h2>
            {pages[formState.currentPage].description && (
              <p className="mt-2 text-gray-600">
                {pages[formState.currentPage].description}
              </p>
            )}
          </div>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1">
          <div ref={formContentRef} className="flex-1 relative">
            <div 
              ref={contentRef} 
              className="w-full h-full relative"
            >
              {pages.map((page, index) => {
                const pageState = getPageState(index);
                const pageFields = fields.filter(f => f.page === index);

                return (
                  <PageTransition
                    key={index}
                    state={pageState}
                    direction={transitionState.direction}
                  >
                    <div className="h-full flex flex-col overflow-auto">
                      <div className="flex-1 p-8">
                        <div className={fieldSpacing === 'even' ? 'space-y-8' : `space-y-${defaultSpacing}`}>
                          {pageFields.map(field => {
                            const Component = (() => {
                              switch (field.type) {
                                case 'name': return NameField;
                                case 'email': return EmailField;
                                case 'phone': return PhoneField;
                                case 'text': return TextFieldWithLength;
                                case 'content-display':
                                case 'custom': return field.customComponent;
                                default: return null;
                              }
                            })();
                            
                            if (!Component) return null;
                            
                            return (
                              <div key={field.id} id={`field-${field.id}`}>
                                <Component
                                  value={formState.data[field.id]}
                                  onChange={handleChange(field.id)}
                                  required={field.required}
                                  label={field.label || field.id}
                                  minLength={field.type === 'text' ? field.minLength : undefined}
                                  maxLength={field.type === 'text' ? field.maxLength : undefined}
                                  placeholder={field.type === 'text' ? field.placeholder : undefined}
                                  contents={field.type === 'content-display' ? field.contents : undefined}
                                />
                                {formState.errors[field.id] && (
                                  <p className="mt-1 text-sm text-red-600">{formState.errors[field.id]}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {buttonPosition === 'after-fields' && <ButtonGroup />}
                      </div>
                    </div>
                  </PageTransition>
                );
              })}
            </div>
          </div>

          {buttonPosition === 'bottom' && (
            <div className={cn(
              "sticky bottom-0 bg-white pt-4 mt-4 border-t w-full",
              fullViewport ? "px-8" : ""
            )}>
              <ButtonGroup />
            </div>
          )}
        </form>
      </div>
    </div>
  </div>
);
};